import { useEffect, useState } from "react";

/**
 * Read the current JCC handover session (the magic-link session set by
 * /api/auth/handover/redeem). Independent from the legacy AuthContext.
 *
 * The backend already returns `entitlements` fully expanded for tier
 * inheritance (pro_carer implies carer_free, family_circle implies
 * family_free — see `expand_entitlements()` in handover.py), so any
 * consumer of this hook can safely do a plain `.includes(tier)` check
 * and automatically respect inheritance without re-implementing it.
 *
 * Returns `{ ready, active, email, entitlements }`.
 *   ready        → the /me probe has completed at least once
 *   active       → boolean, true if the handover cookie resolves to an active member
 *   email        → the member's email address, if the backend returned one
 *   entitlements → sorted array of the member's entitlement strings (pre-expanded)
 */
export function useHandoverSession() {
  const [state, setState] = useState({
    ready: false,
    active: false,
    email: null,
    entitlements: [],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/handover/me`,
          { credentials: "include" }
        );
        if (cancelled) return;
        if (!r.ok) {
          setState({ ready: true, active: false, email: null, entitlements: [] });
          return;
        }
        const body = await r.json().catch(() => ({}));
        const active = !!(body && body.active);
        const ents = Array.isArray(body && body.entitlements) ? body.entitlements : [];
        setState({
          ready: true,
          active,
          email: active && typeof body.email === "string" ? body.email : null,
          entitlements: active ? ents : [],
        });
      } catch (_) {
        if (!cancelled) {
          setState({ ready: true, active: false, email: null, entitlements: [] });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/**
 * Resolve the "My Workspace" destination(s) for a set of (already-expanded)
 * entitlements.
 *
 * A member's entitlements can span two independent tracks — carer and
 * family. Within a track, the paid tier always wins over the free tier
 * (e.g. pro_carer > carer_free). Across tracks, neither is assumed to be
 * "more important": a member holding both a carer and a family tier gets
 * BOTH destinations back, so the caller can render an explicit chooser
 * instead of silently picking one side. Returns an array of
 * `{ tier, label, href }`, in the order [carer destination, family
 * destination] (only the tracks the member actually holds are included).
 */
export function getWorkspaceDestinations(entitlements) {
  const set = new Set(Array.isArray(entitlements) ? entitlements : []);
  const destinations = [];

  if (set.has("pro_carer")) {
    destinations.push({ tier: "pro_carer", label: "Pro Carer Office", href: "/workspace/pro-carer-circle/start-here" });
  }
  if (set.has("carer_essentials")) {
    destinations.push({ tier: "carer_essentials", label: "Carer Essentials", href: "/workspace/carer-essentials" });
  }
  if (set.has("carer_free")) {
    destinations.push({ tier: "carer_free", label: "Carer Starter", href: "/workspace/carer-starter" });
  }

  if (set.has("family_circle")) {
    destinations.push({ tier: "family_circle", label: "Family Care Office", href: "/workspace/family-circle" });
  }
  if (set.has("family_free")) {
    destinations.push({ tier: "family_free", label: "Family Starter", href: "/workspace/family-starter" });
  }

  return destinations;
}
